import { FC } from 'react'

import {
  Text,
  Primary13Regular,
  RedRed60,
  Flex
} from '@sequencehq/core-components'
import { CustomerMultiSelect, CustomerSelect } from 'Customer/index'
import { ValidationResult } from 'Automations/nodeDefinitions/validation'

type BaseProps = {
  'data-testid'?: string
  validationErrors: ValidationResult[] | undefined
}

type MultiSelectProps = BaseProps & {
  multiple: true
  value: string[]
  onChange: (value: string[]) => void
}

type SingleSelectProps = BaseProps & {
  multiple?: false
  value: string
  onChange: (value: string) => void
}

export type CustomerSelectorProps = MultiSelectProps | SingleSelectProps

/**
 * Customer picker shared across single- and multi-select use cases. Set
 * `multiple` to switch between the async single `CustomerSelect` (string value)
 * and the `CustomerMultiSelect` popover (string[] value). Both are search-led
 * and paginated.
 */
export const CustomerSelector: FC<CustomerSelectorProps> = props => {
  const { 'data-testid': dataTestId, validationErrors } = props
  const isInvalid: boolean = !!validationErrors?.at(0)

  if (!props.multiple) {
    return (
      <CustomerSelect
        data-testid={dataTestId}
        value={props.value}
        onChange={props.onChange}
        isInvalid={isInvalid}
        validationErrors={validationErrors?.map(e => ({ message: e.message }))}
        allowCreate={false}
      />
    )
  }

  return (
    <Flex gap="8px" flexDirection="column">
      <CustomerMultiSelect
        data-testid={dataTestId}
        values={Array.isArray(props.value) ? props.value : []}
        onChange={props.onChange}
        isInvalid={isInvalid}
      />

      {validationErrors?.at(0) && (
        <Text {...Primary13Regular} color={RedRed60}>
          {validationErrors.at(0)?.message}
        </Text>
      )}
    </Flex>
  )
}
